import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import "react-datepicker/dist/react-datepicker.css";
import {
  FormContainer,
  FormWrapper,
  GridItem,
  OptionalSection,
  OptionalFieldWrap,
  ActionsRow,
} from "./MetadataForm.elements";
import { UploadTitleBlock, UploadButton } from "../UploadForm.elements";
import { TitleText } from "../../Util/Modal.elements";
import countries from "../../Util/countries.json";
import statesData from "../../Util/states.json";
import { usePackagesActions, useAuthState, useContributorsState, useContributorsActions } from "@/store";
import {
  TextField,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Switch,
  FormControlLabel,
  Autocomplete,
} from "@mui/material";
import UploadStepper from "../UploadStepper";
import MuiClipDateRangePicker from "@/components/Clips/MuiClipDateRangePicker";
import EmbargoDateTimePicker, { nextFullHour } from "../EmbargoDateTimePicker";
import CCUsersComponent from "@/components/Norifications/cc/CCUsersComponent";
import sortCountriesByName from "@/utils/sortCountries";
import { normalizeStateOptions } from "@/utils/states";
import { buildUploadMetadataPayload } from "@/utils/uploadMetadataPayload";

const MetadataForm = ({ files, handleBack, handleNext, metadataDraft, setMetadataDraft }) => {
  const initialCountryCode = metadataDraft?.formValues?.country || "";
  const [states, setStates] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState(initialCountryCode);
  const { uploadPackage } = usePackagesActions();
  const { currentUser } = useAuthState();
  const {
    contributors,
    fetch: { status: contributorsFetchStatus },
  } = useContributorsState();
  const { fetchContributors } = useContributorsActions();
  const [ccEmails, setCCEmails] = useState(metadataDraft?.ccEmails || []);
  const [isCCEnabled, setIsCCEnabled] = useState(Boolean(metadataDraft?.isCCEnabled));
  const [isEmbargoEnabled, setIsEmbargoEnabled] = useState(Boolean(metadataDraft?.isEmbargoEnabled));
  const [isDelegateUpload, setIsDelegateUpload] = useState(Boolean(metadataDraft?.isDelegateUpload));
  const [selectedContributor, setSelectedContributor] = useState(metadataDraft?.selectedContributor || null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const previousCountryCodeRef = useRef(initialCountryCode);
  // Tutorial "Do it for me": the country effect below wipes State/Region when
  // the country changes, so the rest of the practice values are applied from
  // inside that effect (after the wipe) instead of racing it with a timer.
  const pendingTutorialFillRef = useRef(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
    setValue,
    getValues,
    trigger,
    clearErrors,
    unregister,
    watch,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onBlur",
    shouldUnregister: true,
    defaultValues: metadataDraft?.formValues,
  });

  const watchedFormValues = useWatch({ control });
  const embargoValue = watch("embargo");
  const selectedCountryValue = watch("country", initialCountryCode);
  const role = currentUser?.role;
  const canUploadOnBehalf = role === "admin" || role === "manager" || role === "contributor";
  const contributorsLoading = contributorsFetchStatus === "loading";

  const contributorOptions = useMemo(() => contributors || [], [contributors]);
  const sortedCountries = useMemo(() => sortCountriesByName(countries), []);

  const delegateSelectionInvalid = isDelegateUpload && !selectedContributor;
  const isSubmitDisabled = hasSubmitted || isSubmitting || !isValid || (isEmbargoEnabled && !embargoValue) || delegateSelectionInvalid;

  useEffect(() => {
    if (selectedCountryCode) {
      setStates(normalizeStateOptions(statesData[selectedCountryCode] || []));
    } else {
      setStates([]);
    }
    if (previousCountryCodeRef.current !== selectedCountryCode) {
      setValue("state", "");
      clearErrors("state");
    }
    previousCountryCodeRef.current = selectedCountryCode;
    if (pendingTutorialFillRef.current) {
      const applyRest = pendingTutorialFillRef.current;
      pendingTutorialFillRef.current = null;
      applyRest();
    }
  }, [selectedCountryCode, setValue, clearErrors]);

  useEffect(() => {
    if (canUploadOnBehalf && (contributorsFetchStatus === "idle" || contributorsFetchStatus === "failed")) {
      fetchContributors();
    }
  }, [canUploadOnBehalf, contributorsFetchStatus, fetchContributors]);

  useEffect(() => {
    if (!isDelegateUpload) {
      setSelectedContributor(null);
    }
  }, [isDelegateUpload]);

  useEffect(() => {
    if (!canUploadOnBehalf && isDelegateUpload) {
      setIsDelegateUpload(false);
    }
  }, [canUploadOnBehalf, isDelegateUpload]);

  useEffect(() => {
    setMetadataDraft((prev) => ({
      ...prev,
      formValues: {
        ...prev.formValues,
        ...watchedFormValues,
      },
    }));
  }, [watchedFormValues, setMetadataDraft]);

  useEffect(() => {
    setMetadataDraft((prev) => ({
      ...prev,
      ccEmails,
      isCCEnabled,
      isEmbargoEnabled,
      isDelegateUpload,
      selectedContributor,
    }));
  }, [
    ccEmails,
    isCCEnabled,
    isEmbargoEnabled,
    isDelegateUpload,
    selectedContributor,
    setMetadataDraft,
  ]);

  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    setSelectedCountryCode(countryCode);
    setValue("country", countryCode, { shouldValidate: true, shouldDirty: true });
    clearErrors("country");
  };

  // Tutorial hook ("Do it for me" on the fill-the-form step): fill the required
  // fields with clearly-marked practice values so the user sees a valid form.
  // Values are ordinary form input — nothing is submitted here.
  useEffect(() => {
    const fill = () => {
      const opts = { shouldValidate: true, shouldDirty: true, shouldTouch: true };
      const cur = getValues();
      const empty = (v) => v == null || (typeof v === "string" && v.trim() === "");
      // Only fill what the user hasn't typed — their own input is never replaced.
      if (empty(cur.packageName)) setValue("packageName", "PRACTICE — tutorial package (replace before submitting)", opts);
      if (empty(cur.description)) setValue("description", "Practice values from the upload tutorial. Replace with the real story details.", opts);
      const applyRest = () => {
        if (empty(getValues("state"))) setValue("state", "New York", opts);
        if (empty(getValues("city"))) setValue("city", "New York", opts);
        if (!getValues("dateShot")) setValue("dateShot", new Date(), opts);
      };
      if (!empty(cur.country)) {
        applyRest();
        return;
      }
      if (previousCountryCodeRef.current === "US") {
        // Country effect won't run again → apply directly.
        setValue("country", "US", opts);
        applyRest();
      } else {
        pendingTutorialFillRef.current = applyRest;
        setSelectedCountryCode("US");
        setValue("country", "US", opts);
        clearErrors("country");
      }
    };
    // When the tour ends, remove ONLY values that are still the practice text —
    // anything the user typed themselves is left alone.
    const clear = () => {
      const current = getValues();
      const isPractice = (v, marker) => typeof v === "string" && v.startsWith(marker);
      const touched = isPractice(current.packageName, "PRACTICE —") || isPractice(current.description, "Practice values from the upload tutorial");
      if (!touched) return;
      // No validation on clearing: an untouched form must not light up red.
      const opts = { shouldValidate: false, shouldDirty: false };
      if (isPractice(current.packageName, "PRACTICE —")) setValue("packageName", "", opts);
      if (isPractice(current.description, "Practice values from the upload tutorial")) setValue("description", "", opts);
      if (current.country === "US" && current.state === "New York" && current.city === "New York") {
        setValue("city", "", opts);
        setValue("state", "", opts);
        setSelectedCountryCode("");
        setValue("country", "", opts);
        setValue("dateShot", undefined, opts);
      }
      // Recompute validity (Submit must go dark on an emptied form) without
      // leaving red messages behind.
      Promise.resolve(trigger(["packageName", "description", "country", "state", "city", "dateShot"])).then(() =>
        clearErrors(["packageName", "description", "country", "state", "city", "dateShot"])
      );
    };
    window.addEventListener("clipn:tutorial-fill-metadata", fill);
    window.addEventListener("clipn:tutorial-upload-tour-ended", clear);
    return () => {
      window.removeEventListener("clipn:tutorial-fill-metadata", fill);
      window.removeEventListener("clipn:tutorial-upload-tour-ended", clear);
    };
  }, [setValue, clearErrors, getValues, trigger]);

  const formatContributorLabel = (contributor) => {
    if (!contributor) return "";
    const name = contributor?.name?.trim();
    const email = contributor?.email || "";
    if (name && email) return `${name} (${email})`;
    return name || email;
  };

  const onSubmit = (data) => {
    if (hasSubmitted) return;
    setHasSubmitted(true);

    const uploadMetadata = {
      ...buildUploadMetadataPayload(data, sortedCountries),
      ccEmails: isCCEnabled ? ccEmails : [],
      embargo: isEmbargoEnabled ? (data.embargo ?? null) : null,
      ...(canUploadOnBehalf && isDelegateUpload && selectedContributor
        ? {
          otherContributorId: selectedContributor.id,
          contributorName: selectedContributor.name || selectedContributor.email,
        }
        : {}),
    };

    uploadPackage({ files, uploadMetadata });
    handleNext();
  };

  return (
      <FormContainer>
        <UploadTitleBlock>
          <TitleText>ADD METADATA</TitleText>
        </UploadTitleBlock>

        <UploadStepper step={2} />

        <form data-testid="upload-metadata-form" data-tour="metadata-form" onSubmit={handleSubmit(onSubmit)}>
          <FormWrapper>
            {/* Package Name / Description / City read their value back from the form:
                MUI floats the label only for changes it sees, and setValue (tutorial
                "Do it for me", draft restore) writes the input node directly — which
                left the label printed on top of the text. */}
            <GridItem $full>
              <TextField
                  fullWidth
                  label="Package Name*"
                  placeholder="Enter the package name"
                  multiline
                  minRows={1}
                  maxRows={4}
                  sx={{
                    "& .MuiInputBase-inputMultiline": {
                      overflowX: "hidden",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    },
                  }}
                  {...register("packageName", {
                    required: "Package Name is required",
                    maxLength: { value: 200, message: "Package Name must be less than 200 characters long" },
                  })}
                  value={watchedFormValues?.packageName ?? ""}
                  inputProps={{
                    style: {
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    },
                  }}
                  error={!!errors.packageName}
                  helperText={errors.packageName?.message}
              />
            </GridItem>

            <GridItem $full>
              <TextField
                  fullWidth
                  label="Description*"
                  placeholder="Description is required"
                  multiline
                  minRows={3}
                  maxRows={6}
                  sx={{
                    "& .MuiInputBase-inputMultiline": {
                      overflowX: "hidden",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    },
                  }}
                  {...register("description", {
                    required: "Description is required",
                    maxLength: { value: 5000, message: "Package Name must be less than 5000 characters long" },
                  })}
                  value={watchedFormValues?.description ?? ""}
                  inputProps={{
                    style: {
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    },
                  }}
                  error={!!errors.description}
                  helperText={errors.description?.message}
              />
            </GridItem>

            <GridItem>
              <FormControl fullWidth error={!!errors.country}>
                <InputLabel id="country-select-label">Country*</InputLabel>
                <MuiSelect
                    labelId="country-select-label"
                    label="Country*"
                    value={selectedCountryValue || ""}
                    {...register("country", { required: "Country is required" })}
                    onChange={handleCountryChange}
                >
                  {sortedCountries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name}
                      </MenuItem>
                  ))}
                </MuiSelect>
                {!!errors.country && <FormHelperText>{errors.country?.message}</FormHelperText>}
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl fullWidth>
                <Controller
                    name="state"
                    control={control}
                    defaultValue=""
                    {...register("state", {
                      required: "State/Region is required",
                    })}
                    render={({ field }) => (
                        <Autocomplete
                            freeSolo
                            options={states}
                            disabled={!selectedCountryCode}
                            value={field.value || ""}
                            inputValue={field.value || ""}
                            onChange={(_, newValue) => field.onChange(newValue || "")}
                            onInputChange={(_, newInputValue) => field.onChange(newInputValue || "")}
                            noOptionsText="Type your region"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="State/Region*"
                                    placeholder={selectedCountryCode ? "Enter state or region" : "Select country first"}
                                    error={!!errors.state}
                                    helperText={errors.state?.message}
                                />
                            )}
                        />
                    )}
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <TextField
                  fullWidth
                  label="City*"
                  placeholder="City is required"
                  {...register("city", { required: "Enter the city" })}
                  value={watchedFormValues?.city ?? ""}
                  error={!!errors.city}
                  helperText={errors.city?.message}
              />
            </GridItem>

            <GridItem>
              <FormControl fullWidth>
                <Controller
                    name="dateShot"
                    control={control}
                    defaultValue={undefined}
                    rules={{ required: "Date is required" }}
                    render={({ field }) => (
                        <MuiClipDateRangePicker
                            label="Shot Date*"
                            value={field.value}
                            onChange={(dateShot) => field.onChange(dateShot)}
                            format="yyyy/MM/dd"
                            slotProps={{
                              textField: {
                                placeholder: "YYYY/MM/DD",
                              },
                            }}
                        />
                    )}
                />
                {!!errors.dateShot && <FormHelperText>{errors.dateShot?.message}</FormHelperText>}
              </FormControl>
            </GridItem>

            <GridItem $full>
              <TextField
                  label="Notes"
                  placeholder="Add any notes"
                  multiline
                  minRows={3}
                  maxRows={6}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-inputMultiline": {
                      overflowX: "hidden",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    },
                  }}
                  inputProps={{
                    style: {
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    },
                  }}
                  {...register("notes")}
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
              />
            </GridItem>

            <GridItem $full>
              <OptionalSection data-tour="metadata-optional">
                {canUploadOnBehalf && (
                    <div>
                      <FormControlLabel
                          sx={{ width: "100%", m: 0, alignItems: "center" }}
                          control={
                            <Switch
                                checked={isDelegateUpload}
                                onChange={() => setIsDelegateUpload((prev) => !prev)}
                            />
                          }
                          label="Upload on behalf of another contributor"
                      />

                      {isDelegateUpload && (
                          <OptionalFieldWrap>
                            <Autocomplete
                                value={selectedContributor}
                                onChange={(event, value) => setSelectedContributor(value)}
                                options={contributorOptions}
                                getOptionLabel={formatContributorLabel}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                loading={contributorsLoading}
                                noOptionsText="No contributors found"
                                fullWidth
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Contributor"
                                        placeholder="Search contributors"
                                        error={delegateSelectionInvalid}
                                        helperText={delegateSelectionInvalid ? "Contributor is required" : ""}
                                    />
                                )}
                            />
                          </OptionalFieldWrap>
                      )}
                    </div>
                )}

                <div data-tour="metadata-embargo">
                  <FormControlLabel
                      sx={{ width: "100%", m: 0, alignItems: "center" }}
                      control={
                        <Switch
                            checked={isEmbargoEnabled}
                            onChange={() => {
                              setIsEmbargoEnabled((prev) => {
                                const next = !prev;
                                if (!next) {
                                  unregister("embargo");
                                  clearErrors("embargo");
                                  setValue("embargo", undefined, { shouldValidate: false, shouldDirty: true });
                                } else {
                                  // Start at the next full hour, like the iOS app, so the
                                  // field is valid right away and reads cleanly.
                                  setValue("embargo", nextFullHour(), { shouldValidate: true, shouldDirty: true });
                                }
                                return next;
                              });
                            }}
                        />
                      }
                      label="Set Embargo Date & Time"
                  />

                  {isEmbargoEnabled && (
                      <OptionalFieldWrap>
                        <FormControl fullWidth>
                          <Controller
                              name="embargo"
                              control={control}
                              shouldUnregister
                              rules={{
                                validate: (v) => {
                                  if (!isEmbargoEnabled) return true;
                                  if (!v || Number.isNaN(new Date(v).getTime())) return "Embargo date and time are required";
                                  if (new Date(v).getTime() <= Date.now()) return "Embargo must be in the future";
                                  return true;
                                },
                              }}
                              render={({ field }) => (
                                  <EmbargoDateTimePicker
                                      label="Embargo until*"
                                      value={field.value}
                                      onChange={(embargo) => field.onChange(embargo ?? undefined)}
                                      error={!!errors.embargo}
                                      helperText={errors.embargo?.message}
                                  />
                              )}
                          />
                        </FormControl>
                      </OptionalFieldWrap>
                  )}
                </div>

                <div data-tour="metadata-notify">
                  <FormControlLabel
                      sx={{ width: "100%", m: 0, alignItems: "center" }}
                      control={<Switch checked={isCCEnabled} onChange={() => setIsCCEnabled((prev) => !prev)} />}
                      label="Notify your assignment editor"
                  />

                  {isCCEnabled && (
                      <OptionalFieldWrap>
                        <CCUsersComponent
                            onCCUpdate={setCCEmails}
                            selectedEmails={ccEmails}
                        />
                      </OptionalFieldWrap>
                  )}
                </div>
              </OptionalSection>
            </GridItem>
          </FormWrapper>

          <ActionsRow>
            <UploadButton variant="outlined" color="white" onClick={handleBack} type="button">
              Back
            </UploadButton>
            <UploadButton
                buttonState="next"
                type="submit"
                disabled={isSubmitDisabled}
                data-testid="upload-start-button"
                data-tour="upload-start-button"
            >
              Submit
            </UploadButton>
          </ActionsRow>
        </form>
      </FormContainer>
  );
};

export default MetadataForm;


